import {
  AddBusinessListingModal,
  OperatorDoorstopEmptyCard,
} from "apps/home/components";
import { AssetImage, EnumOwnerType } from "gql/generated";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import {
  DoorstopCard,
  DoorstopLayout,
  EmptyState,
  LoadingPage,
  ViewSubSection,
} from "shared/components";
import { getAddressString, getMultilingualString } from "shared/utils";
import { Grid } from "styled-system/jsx";
import { useAppDoorstop } from "./hooks";

export const AppDoorstop = () => {
  const { t } = useTranslation("core");
  const {
    error,
    loading,
    onItemClick,
    operators,
    organizations,
    isModalOpen,
    setIsModalOpen,
  } = useAppDoorstop();

  if (loading) {
    return <LoadingPage />;
  }

  if (error) {
    throw new Error("Could not load user data");
  }

  /**
   * Build the organization and operator groups for the UI
   */
  const groups = [
    {
      title: t("DOORSTOP.ORGANIZATIONS.TITLE"),
      description: t("DOORSTOP.ORGANIZATIONS.DESCRIPTION"),
      items: _.sortBy(
        organizations?.map((org) => ({
          _entityId: org._id,
          path: `/app/organization/${org._id}`,
          name: getMultilingualString(org.name),
          image: org.logo as AssetImage,
          type: EnumOwnerType.Organization,
        })) || [],
        "name",
      ),
    },
    {
      title: t("DOORSTOP.OPERATORS.TITLE"),
      description: t("DOORSTOP.OPERATORS.DESCRIPTION"),
      items: _.sortBy(
        operators?.map((op) => ({
          _entityId: op._entityId,
          path: `/app/operator/${op._entityId}`,
          name: getMultilingualString(op.name),
          image: op.images?.[0] as AssetImage,
          type: EnumOwnerType.Operator,
          claimed: op.claimed,
          address: op.contact.address
            ? getAddressString(op.contact.address)
            : undefined,
        })) || [],
        "name",
      ),
    },
  ];

  const totalItems = groups.reduce((prev, cur) => prev + cur.items.length, 0);

  const handleAddNewListingClick = () => setIsModalOpen(true);

  return (
    <>
      <DoorstopLayout>
        {totalItems === 0 && (
          <EmptyState text={t("ERROR.NO_ACCESS")} maxW="1100px" />
        )}
        {groups.map((group) => {
          const isOperatorGroup =
            group.items.find((v) => v)?.type === EnumOwnerType.Operator;
          return (
            group.items.length > 0 && (
              <ViewSubSection
                key={group.title}
                title={group.title}
                description={group.description}
                buttonLabel={
                  isOperatorGroup
                    ? `+ ${t("DOORSTOP.OPERATORS.ADD_NEW_LISTING")}`
                    : undefined
                }
                onButtonClick={
                  isOperatorGroup ? handleAddNewListingClick : undefined
                }
                width="full"
                maxW="800px"
                gap={"300"}
              >
                <Grid
                  gridTemplateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
                  gap={"300"}
                >
                  {group.items.map((item) => (
                    <DoorstopCard
                      key={item._entityId}
                      doorstopItem={item}
                      onItemClick={onItemClick}
                    />
                  ))}
                  {isOperatorGroup && (
                    <OperatorDoorstopEmptyCard
                      numOfOperators={group.items.length}
                      onAddListingClick={handleAddNewListingClick}
                    />
                  )}
                </Grid>
              </ViewSubSection>
            )
          );
        })}
      </DoorstopLayout>
      <AddBusinessListingModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </>
  );
};
